import { getMomentTz } from "@dexteel/mesf-core";

const moment = getMomentTz();

/**
 * Returns true when the event (using OriginalStart/OriginalEnd) spans two calendar months.
 */
export const isMultiMonthEvent = (
	originalStart: Date | undefined,
	originalEnd: Date | undefined,
): boolean => {
	if (!originalStart || !originalEnd) return false;
	return moment(originalStart).month() !== moment(originalEnd).month();
};

/**
 * Returns the fraction of the event duration that falls in the given calendar month.
 * calendarMonth is 0-indexed (same as Date.getMonth()).
 */
export const getEventMonthRatio = (
	originalStart: Date,
	originalEnd: Date,
	calendarMonth: number,
): number => {
	const totalHours = moment(originalEnd).diff(
		moment(originalStart),
		"hours",
		true,
	);
	if (totalHours <= 0) return 1;
	const boundary = moment(originalStart)
		.clone()
		.add(1, "month")
		.startOf("month");
	const hoursInFirst = Math.max(
		0,
		Math.min(totalHours, boundary.diff(moment(originalStart), "hours", true)),
	);
	const ratio1 = hoursInFirst / totalHours;
	return moment(originalStart).month() === calendarMonth ? ratio1 : 1 - ratio1;
};

/**
 * Splits a total of rolling hours between the two calendar months an event spans.
 * The first month can only absorb as many rolling hours as the event physically
 * occupies within it (its remaining hours); the surplus spills into the next month.
 * Returns [firstMonthHours, secondMonthHours].
 */
export const splitRollingHoursByMonth = (
	start: Date,
	end: Date,
	monthBoundary: Date,
	totalRollingHours: number,
): [number, number] => {
	const totalHours = moment(end).diff(moment(start), "hours", true);
	if (totalHours <= 0 || totalRollingHours <= 0) return [totalRollingHours, 0];

	const hoursInFirst = Math.max(
		0,
		Math.min(
			totalHours,
			moment(monthBoundary).diff(moment(start), "hours", true),
		),
	);
	const firstHours = Math.min(totalRollingHours, hoursInFirst);
	return [firstHours, totalRollingHours - firstHours];
};

/**
 * Fraction of an event's scheduled values that belongs to the given calendar month.
 * Rolling hours are capped at each month's remaining hours, and tons follow that same
 * split so tonnage and hours stay consistent.
 */
export const getEventMonthScheduledRatio = (
	originalStart: Date,
	originalEnd: Date,
	calendarMonth: number,
	totalRollingHours: number | null | undefined,
): number => {
	if (!totalRollingHours || totalRollingHours <= 0) {
		return getEventMonthRatio(originalStart, originalEnd, calendarMonth);
	}
	const monthBoundary = moment(originalStart)
		.clone()
		.add(1, "month")
		.startOf("month")
		.toDate();
	const [firstHours] = splitRollingHoursByMonth(
		originalStart,
		originalEnd,
		monthBoundary,
		totalRollingHours,
	);
	const firstRatio = firstHours / totalRollingHours;
	return moment(originalStart).month() === calendarMonth
		? firstRatio
		: 1 - firstRatio;
};
